import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, finalize, from, switchMap } from 'rxjs';
import { ConfirmDialog } from '../../../common/interfaces/confirm-dialog.interface';
import { MentorConnectedUser } from '../../../interfaces/responsedata.ts/mentor';
import { DialogService } from '../../../service/dialog.service';
import { MentorService } from '../../../service/mentor.service';

@Component({
    selector: 'app-active-founder',
    standalone: false,
    templateUrl: './active-founder.component.html',
    styleUrl: './active-founder.component.scss',
})
export class ActiveFounderComponent {
    @Input({ required: true })
    public activeFounder!: MentorConnectedUser;

    @Output()
    public isLoading = new EventEmitter<boolean>(false);

    @Output()
    public revokedFounder = new EventEmitter<string>();

    private destroyRef = inject(DestroyRef);
    private mentorService = inject(MentorService);
    private dialogService = inject(DialogService);

    public checkIfUserWaitsOnGate(): boolean {
        if (!this.activeFounder.userGateProgress?.length) return false;

        for (let i = 0; i < this.activeFounder.userGateProgress?.length; i++) {
            if (
                this.activeFounder.userGateProgress[i].closed === false &&
                this.activeFounder.userGateProgress[i].confirmedByCoach === false &&
                this.activeFounder.userGateProgress[i-1].closed === true &&
                this.activeFounder.userGateProgress[i-1].confirmedByCoach === true
            ) {
                return true;
            }
        }

        return false;
    }

    protected revokeAccess(activeFounder: MentorConnectedUser): void {
        const dialogData: ConfirmDialog = {
            headerText: 'Revoke access?',
            dialogText:
                'Are you sure that you want to revoke the access to this founder? By doing so, you will be granted a new seat on your current licence.',
            submitText: 'Yes, revoke',
            cancelText: 'Cancel',
        };

        this.dialogService
            .openConfirmationDialog$(dialogData)
            .pipe(
                switchMap((confirm) => {
                    this.isLoading.next(true);

                    if (!confirm) {
                        return EMPTY;
                    }

                    return from(
                        this.mentorService.revokeAccessByFounderId(
                            activeFounder.id,
                        ),
                    );
                }),
                finalize(() => this.isLoading.next(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (revokeSuccess) => {
                    if (!revokeSuccess) {
                        return;
                    }

                    this.revokedFounder.next(activeFounder.id);
                },
            });
    }

    protected showDetails(activeFounder: MentorConnectedUser): void {
        this.dialogService
            .openFounderDetailsDialog$(activeFounder)
            .pipe(
                switchMap((confirm) => {
                    this.isLoading.next(true);

                    if (!confirm.revoked) {
                        return EMPTY;
                    }

                    return from(
                        this.mentorService.revokeAccessByFounderId(
                            activeFounder.id,
                        ),
                    );
                }),
                finalize(() => this.isLoading.next(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (revokeSuccess) => {
                    if (!revokeSuccess) {
                        return;
                    }

                    this.revokedFounder.next(activeFounder.id);
                },
            });
    }
}
